import { useId, useRef } from "react";
import { cn } from "@/lib/utils";

/**
 * A row of mutually exclusive options that reads as buttons but behaves
 * as a radio group: one tab stop, arrow keys move the choice, and
 * screen readers hear "Counted by, radio group, Minutes read, selected".
 *
 * options: [{ value, label, icon, hint }] — a hint is a second, smaller
 * line under the label (e.g. "0.6″ sides").
 */
export default function SegmentedChoice({ label, value, onChange, options, disabled = false, note }) {
  const labelId = useId();
  const refs = useRef([]);

  const move = (index) => {
    const next = (index + options.length) % options.length;
    onChange(options[next].value);
    refs.current[next]?.focus();
  };

  return (
    <div>
      <p id={labelId} className="mb-2 text-sm font-medium text-foreground">{label}</p>
      <div role="radiogroup" aria-labelledby={labelId} className="flex flex-col gap-2 sm:flex-row">
        {options.map((option, i) => {
          const selected = option.value === value;
          const Icon = option.icon;
          return (
            <button
              key={option.value}
              ref={(el) => (refs.current[i] = el)}
              type="button"
              role="radio"
              aria-checked={selected}
              tabIndex={selected ? 0 : -1}
              disabled={disabled && !selected}
              onClick={() => onChange(option.value)}
              onKeyDown={(e) => {
                if (e.key === "ArrowRight" || e.key === "ArrowDown") { e.preventDefault(); move(i + 1); }
                if (e.key === "ArrowLeft" || e.key === "ArrowUp") { e.preventDefault(); move(i - 1); }
              }}
              className={cn(
                "flex min-h-11 flex-1 items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors",
                option.hint ? "justify-start text-left" : "justify-center",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
                selected ? "border-primary bg-primary/10 text-primary" : "border-border bg-card text-muted-foreground hover:text-foreground"
              )}
            >
              {Icon && <Icon className="h-4 w-4" aria-hidden="true" />}
              {option.hint ? (
                <span>
                  <span className="block">{option.label}</span>
                  <span className="block text-xs font-normal text-muted-foreground">{option.hint}</span>
                </span>
              ) : option.label}
            </button>
          );
        })}
      </div>
      {note && <p className="mt-2 text-xs text-muted-foreground">{note}</p>}
    </div>
  );
}
